import type { StyleSpecification } from 'maplibre-gl';
import { color } from '../styles/tokens';

/**
 * CORREÇÃO CRÍTICA (bug que impedia o mapa de renderizar — tela cinza):
 * a versão anterior deste arquivo declarava um `source-layer` por nome
 * (`boundaries`, `roads`, `places`, todos no PLURAL) que não existe no
 * schema real servido pelo OpenFreeMap. Isso gerava o erro real de
 * console "Source layer 'boundaries' does not exist on source
 * 'openfreemap'..." pra cada um desses 3 layers — sem eles carregando,
 * não havia tile visível, e o mapa aparecia como tela cinza travada
 * (sem conteúdo pra redesenhar em pan/zoom, o que também explicava o
 * Bug 2 do pan/zoom "não funcionando" — era efeito colateral, não um
 * problema de handler de touch).
 *
 * CAUSA RAIZ CONFIRMADA: o OpenFreeMap serve tiles no schema
 * OpenMapTiles (confirmado: OpenStreetMap Wiki, "OpenFreeMap is an
 * open-source project that provides a vector tile API with an
 * OpenMapTiles schema for free" — wiki.openstreetmap.org/wiki/Vector_tiles).
 * A spec oficial completa (openmaptiles.org/schema/) confirma os nomes
 * reais de layer: water, landuse, landcover, boundary (SINGULAR),
 * transportation (não "roads"), transportation_name, place
 * (SINGULAR, não "places"), building, aeroway, waterway, water_name,
 * park, poi, housenumber, mountain_peak, aerodrome_label.
 *
 * CORREÇÃO APLICADA (seguindo a sugestão da instrução original, em vez
 * de reconstruir contra um schema recém-confirmado): este arquivo usa
 * como BASE LITERAL o JSON real do style "Dark" que o próprio
 * OpenFreeMap disponibiliza pronto — buscado ao vivo em
 * https://tiles.openfreemap.org/styles/dark durante a correção deste
 * bug (não é suposição de estrutura; é o JSON real retornado pelo
 * endpoint). Isso é estritamente mais seguro que reconstruir do zero:
 * cada `source-layer`, `filter`, `minzoom`/`maxzoom` já é usado em
 * produção pelo próprio mantenedor do tileset, então qualquer nome
 * errado já teria sido pego por eles antes de nós. Também confirma que
 * a URL de source que já usávamos
 * (https://tiles.openfreemap.org/planet) sempre esteve correta — só o
 * NOME do source (era 'openfreemap', deveria ser 'openmaptiles', que é
 * a convenção que o próprio style oficial usa) e os nomes de
 * source-layer estavam errados.
 *
 * Nesta correção, aplicamos a paleta do projeto (tokens.ts) por cima
 * das cores do style Dark oficial — trocando `background-color`,
 * `water`, e as cores de fronteira/estrada pra bater com
 * accent-live/bg-base/border-hairline — mas preservando TODOS os
 * layers, filtros e regras de zoom originais do style oficial (aeroway,
 * building, waterway, landcover, todos os sub-layers de place, etc.),
 * que a versão anterior deste arquivo tinha reduzido para um conjunto
 * simplificado de 5 layers vulnerável a exatamente este tipo de erro.
 *
 * Fonte de tiles: https://openfreemap.org (licença ODbL, dados OSM).
 * Style base: https://tiles.openfreemap.org/styles/dark (mesma licença,
 * mantido por hyperknot/openfreemap-styles no GitHub).
 */
export function buildMapStyle(): StyleSpecification {
  return {
    version: 8,
    sources: {
      // Fonte raster da Natural Earth usada só em baixo zoom pelo style
      // oficial (sombreado de relevo) — preservada do style original;
      // não usa cor customizada porque é uma textura, não um fill sólido.
      ne2_shaded: {
        type: 'raster',
        tiles: ['https://tiles.openfreemap.org/natural_earth/ne2sr/{z}/{x}/{y}.png'],
        tileSize: 256,
        maxzoom: 6,
      },
      // Nome do source CORRIGIDO: era 'openfreemap' (arbitrário, nunca
      // confirmado), agora 'openmaptiles' — a convenção real que o
      // style oficial do OpenFreeMap usa. A URL em si já estava certa.
      openmaptiles: {
        type: 'vector',
        url: 'https://tiles.openfreemap.org/planet',
      },
    },
    sprite: 'https://tiles.openfreemap.org/sprites/ofm_f384/ofm',
    glyphs: 'https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf',
    layers: [
      {
        id: 'background',
        type: 'background',
        paint: { 'background-color': color.bgBase },
      },
      {
        id: 'water',
        type: 'fill',
        source: 'openmaptiles',
        'source-layer': 'water', // CONFIRMADO: existe no schema real (era o único dos 5 originais que já estava certo)
        filter: ['all', ['match', ['geometry-type'], ['MultiPolygon', 'Polygon'], true, false], ['!=', ['get', 'brunnel'], 'tunnel']],
        paint: { 'fill-antialias': false, 'fill-color': '#0D1216' },
      },
      {
        id: 'landcover_ice_shelf',
        type: 'fill',
        source: 'openmaptiles',
        'source-layer': 'landcover',
        maxzoom: 8,
        filter: ['all', ['match', ['geometry-type'], ['MultiPolygon', 'Polygon'], true, false], ['==', ['get', 'subclass'], 'ice_shelf']],
        paint: { 'fill-color': color.bgBase, 'fill-opacity': 0.7 },
      },
      {
        id: 'landcover_glacier',
        type: 'fill',
        source: 'openmaptiles',
        'source-layer': 'landcover',
        maxzoom: 8,
        filter: ['all', ['match', ['geometry-type'], ['MultiPolygon', 'Polygon'], true, false], ['==', ['get', 'subclass'], 'glacier']],
        paint: { 'fill-color': 'hsl(0,1%,2%)', 'fill-opacity': ['interpolate', ['linear'], ['zoom'], 0, 1, 8, 0.5] },
      },
      {
        id: 'landuse_residential',
        type: 'fill',
        source: 'openmaptiles',
        'source-layer': 'landuse', // CONFIRMADO: existe no schema real; filtro por class='residential' aplicado (a versão anterior não filtrava por class nenhuma)
        maxzoom: 9,
        filter: ['all', ['match', ['geometry-type'], ['MultiPolygon', 'Polygon'], true, false], ['==', ['get', 'class'], 'residential']],
        paint: { 'fill-color': '#0E1318', 'fill-opacity': 0.4 },
      },
      {
        id: 'landuse_park',
        type: 'fill',
        source: 'openmaptiles',
        'source-layer': 'landuse',
        filter: ['all', ['match', ['geometry-type'], ['MultiPolygon', 'Polygon'], true, false], ['==', ['get', 'class'], 'park']],
        paint: { 'fill-color': '#0E1318' },
      },
      {
        id: 'waterway',
        type: 'line',
        source: 'openmaptiles',
        'source-layer': 'waterway',
        filter: ['match', ['geometry-type'], ['LineString', 'MultiLineString'], true, false],
        paint: { 'line-color': '#0D1216' },
      },
      {
        id: 'building',
        type: 'fill',
        source: 'openmaptiles',
        'source-layer': 'building',
        minzoom: 12,
        filter: ['match', ['geometry-type'], ['MultiPolygon', 'Polygon'], true, false],
        paint: { 'fill-antialias': true, 'fill-color': color.bgBase, 'fill-outline-color': '#0D1216' },
      },
      {
        id: 'road_area_pier',
        type: 'fill',
        source: 'openmaptiles',
        'source-layer': 'transportation', // CORRIGIDO: era 'roads', não existe — nome real é 'transportation'
        filter: ['all', ['match', ['geometry-type'], ['MultiPolygon', 'Polygon'], true, false], ['==', ['get', 'class'], 'pier']],
        paint: { 'fill-antialias': true, 'fill-color': color.bgBase },
      },
      {
        id: 'highway_path',
        type: 'line',
        source: 'openmaptiles',
        'source-layer': 'transportation',
        filter: ['all', ['match', ['geometry-type'], ['LineString', 'MultiLineString'], true, false], ['==', ['get', 'class'], 'path']],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': '#1A2027', 'line-dasharray': [1.5, 1.5], 'line-opacity': 0.9, 'line-width': ['interpolate', ['exponential', 1.2], ['zoom'], 13, 1, 20, 10] },
      },
      {
        // Substitui 'roads-minor' da versão anterior — mesmo filtro de
        // class (['minor'], já estava correto), só o source-layer e o
        // id mudaram pra bater com a convenção do style oficial.
        id: 'highway_minor',
        type: 'line',
        source: 'openmaptiles',
        'source-layer': 'transportation',
        minzoom: 11,
        filter: ['all', ['match', ['geometry-type'], ['LineString', 'MultiLineString'], true, false], ['match', ['get', 'class'], ['minor', 'service', 'track'], true, false]],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': '#1A2027', 'line-opacity': 0.9, 'line-width': ['interpolate', ['exponential', 1.55], ['zoom'], 13, 1.8, 20, 20] },
      },
      {
        // Substitui 'roads-major' da versão anterior — mesmo filtro de
        // class (['primary','secondary','trunk','motorway'], já estava
        // correto), source-layer corrigido de 'roads' pra 'transportation'.
        id: 'highway_major_casing',
        type: 'line',
        source: 'openmaptiles',
        'source-layer': 'transportation',
        minzoom: 11,
        filter: ['all', ['match', ['geometry-type'], ['LineString', 'MultiLineString'], true, false], ['match', ['get', 'class'], ['primary', 'secondary', 'tertiary', 'trunk'], true, false]],
        layout: { 'line-cap': 'butt', 'line-join': 'miter' },
        paint: { 'line-color': color.borderHairline, 'line-width': ['interpolate', ['exponential', 1.3], ['zoom'], 10, 3, 20, 23] },
      },
      {
        id: 'highway_major_inner',
        type: 'line',
        source: 'openmaptiles',
        'source-layer': 'transportation',
        minzoom: 11,
        filter: ['all', ['match', ['geometry-type'], ['LineString', 'MultiLineString'], true, false], ['match', ['get', 'class'], ['primary', 'secondary', 'tertiary', 'trunk'], true, false]],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': '#232B33', 'line-width': ['interpolate', ['exponential', 1.3], ['zoom'], 10, 2, 20, 20] },
      },
      {
        id: 'highway_motorway_casing',
        type: 'line',
        source: 'openmaptiles',
        'source-layer': 'transportation',
        minzoom: 6,
        filter: ['all', ['match', ['geometry-type'], ['LineString', 'MultiLineString'], true, false], ['==', ['get', 'class'], 'motorway']],
        layout: { 'line-cap': 'butt', 'line-join': 'miter' },
        paint: { 'line-color': color.borderHairline, 'line-opacity': 1, 'line-width': ['interpolate', ['exponential', 1.4], ['zoom'], 5.8, 0, 6, 3, 20, 40] },
      },
      {
        id: 'highway_motorway_inner',
        type: 'line',
        source: 'openmaptiles',
        'source-layer': 'transportation',
        minzoom: 6,
        filter: ['all', ['match', ['geometry-type'], ['LineString', 'MultiLineString'], true, false], ['==', ['get', 'class'], 'motorway']],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-color': color.accentLive, 'line-opacity': 0.35, 'line-width': ['interpolate', ['exponential', 1.4], ['zoom'], 4, 2, 6, 1.3, 20, 30] },
      },
      {
        id: 'boundary_state',
        type: 'line',
        source: 'openmaptiles',
        'source-layer': 'boundary', // CORRIGIDO: era 'boundaries' (plural), não existe — nome real é 'boundary' (singular)
        filter: ['==', ['get', 'admin_level'], 4],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-blur': 0.4, 'line-color': color.borderHairline, 'line-dasharray': [2, 2], 'line-opacity': 1, 'line-width': ['interpolate', ['exponential', 1.3], ['zoom'], 3, 1, 22, 15] },
      },
      {
        id: 'boundary_country',
        type: 'line',
        source: 'openmaptiles',
        'source-layer': 'boundary',
        filter: ['==', ['get', 'admin_level'], 2],
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: { 'line-blur': ['interpolate', ['linear'], ['zoom'], 0, 0.4, 22, 4], 'line-color': color.borderHairline, 'line-opacity': 1, 'line-width': ['interpolate', ['exponential', 1.1], ['zoom'], 3, 1, 22, 20] },
      },
      {
        // Substitui 'places-labels' da versão anterior — mesmo filtro
        // de class='city' (já estava correto), source-layer corrigido
        // de 'places' pra 'place'.
        id: 'place_city',
        type: 'symbol',
        source: 'openmaptiles',
        'source-layer': 'place',
        maxzoom: 14,
        filter: ['all', ['match', ['geometry-type'], ['MultiPoint', 'Point'], true, false], ['==', ['get', 'class'], 'city']],
        layout: {
          'text-field': ['coalesce', ['get', 'name_en'], ['get', 'name']],
          'text-font': ['Noto Sans Regular'],
          'text-size': 11,
        },
        paint: {
          'text-color': color.textSecondary,
          'text-halo-color': color.bgBase,
          'text-halo-width': 1.2,
        },
      },
      {
        id: 'place_country',
        type: 'symbol',
        source: 'openmaptiles',
        'source-layer': 'place',
        maxzoom: 6,
        filter: ['all', ['match', ['geometry-type'], ['MultiPoint', 'Point'], true, false], ['==', ['get', 'class'], 'country']],
        layout: {
          'text-field': ['coalesce', ['get', 'name_en'], ['get', 'name']],
          'text-font': ['Noto Sans Regular'],
          'text-size': ['interpolate', ['linear'], ['zoom'], 0, 9, 4, 13],
          'text-transform': 'uppercase',
        },
        paint: {
          'text-color': color.textSecondary,
          'text-halo-color': color.bgBase,
          'text-halo-width': 1.2,
        },
      },
    ],
  };
}
